import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { User, Lock, LogIn, Shield, ArrowLeft, Fingerprint, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export default function UserLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passkeysAvailable, setPasskeysAvailable] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);

  const checkPasskeys = useCallback(async () => {
    if (!window.PublicKeyCredential) return;
    try {
      const res = await axios.get(`${API}/user/passkeys/available`);
      setPasskeysAvailable(res.data.available);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { checkPasskeys(); }, [checkPasskeys]);

  const loginWithPasskey = async () => {
    if (!passkeysAvailable) {
      toast.error("Aucune clé d'accès enregistrée. Configurez-en une depuis votre compte.");
      return;
    }
    setIsPasskeyLoading(true);
    try {
      const optRes = await axios.post(`${API}/user/passkeys/auth-options`);
      const options = JSON.parse(optRes.data.options);
      const authId = optRes.data.auth_id;

      options.challenge = base64urlToBuffer(options.challenge);
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(c => ({
          ...c, id: base64urlToBuffer(c.id)
        }));
      }

      const credential = await navigator.credentials.get({ publicKey: options });

      const credentialJSON = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64url(credential.response.authenticatorData),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : undefined,
        },
      };

      const res = await axios.post(`${API}/user/passkeys/auth-verify`, {
        credential: credentialJSON,
        auth_id: authId,
      });

      localStorage.setItem("user_token", res.data.token);
      localStorage.setItem("user_expires", res.data.expires);
      localStorage.setItem("user_username", res.data.username);
      toast.success(`Bienvenue, ${res.data.username}`);
      onLogin();
    } catch (err) {
      if (err.name === "NotAllowedError") {
        // User cancelled
      } else {
        toast.error(err.response?.data?.detail || "Erreur d'authentification");
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setIsLoading(true);
    try {
      const deviceToken = localStorage.getItem("device_trust_token") || undefined;
      const res = await axios.post(`${API}/user/login`, {
        username: username.trim(),
        password,
        device_token: deviceToken,
      });

      if (res.data.requires_2fa) {
        setPendingToken(res.data.pending_token);
        setRequires2FA(true);
        setIsLoading(false);
        return;
      }

      localStorage.setItem("user_token", res.data.token);
      localStorage.setItem("user_expires", res.data.expires);
      localStorage.setItem("user_username", res.data.username);
      toast.success(`Bienvenue, ${res.data.username}`);
      onLogin();
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur de connexion";
      toast.error(detail);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode.trim()) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/user/2fa/verify-login`, {
        pending_token: pendingToken,
        code: twoFACode.trim(),
        remember_device: rememberDevice,
      });

      if (res.data.device_token) {
        localStorage.setItem("device_trust_token", res.data.device_token);
      }

      localStorage.setItem("user_token", res.data.token);
      localStorage.setItem("user_expires", res.data.expires);
      localStorage.setItem("user_username", res.data.username);

      if (res.data.used_recovery_code) {
        toast.info("Code de récupération utilisé. Pensez à en régénérer.");
      }

      toast.success(`Bienvenue, ${res.data.username}`);
      onLogin();
    } catch (error) {
      const detail = error.response?.data?.detail || "Code invalide";
      toast.error(detail);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-animated flex items-center justify-center p-4 relative overflow-hidden">

      <div className="w-full max-w-sm relative z-10">
        {/* Logo & branding */}
        <div className="text-center mb-8">
          <div className="relative inline-flex">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/20 via-blue-500/15 to-purple-500/10 flex items-center justify-center mx-auto mb-5 border border-cyan-500/20 backdrop-blur-sm shadow-lg shadow-cyan-500/5">
              {requires2FA ? (
                <Shield className="w-9 h-9 text-cyan-400" />
              ) : (
                <Mail className="w-9 h-9 text-cyan-400" />
              )}
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-zinc-950 animate-pulse" />
          </div>
          <h1 className="text-2xl font-mono font-bold text-white tracking-tight">
            {requires2FA ? "Vérification 2FA" : "ShadowMail"}
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            {requires2FA
              ? "Entrez le code de votre application d'authentification"
              : "Connectez-vous à votre espace sécurisé"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-800/80 rounded-2xl p-6 shadow-2xl shadow-black/40">
          {requires2FA ? (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Code d'authentification</label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  className="bg-zinc-800/50 border-zinc-700/50 text-white h-12 font-mono text-center text-lg tracking-[0.3em] focus:border-cyan-500/50 focus:ring-cyan-500/20 transition-colors"
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-cyan-500 focus:ring-cyan-500/30"
                />
                <span className="text-xs text-zinc-400 group-hover:text-white transition-colors">
                  Se souvenir de cet appareil (30 jours)
                </span>
              </label>

              <Button
                type="submit"
                disabled={isLoading || !twoFACode.trim()}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black font-mono font-semibold h-12 rounded-xl shadow-lg shadow-cyan-500/20 transition-colors hover:shadow-cyan-500/30 "
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                Vérifier
              </Button>

              <button
                type="button"
                onClick={() => { setRequires2FA(false); setTwoFACode(""); setPendingToken(""); }}
                className="w-full flex items-center justify-center gap-1.5 text-zinc-500 hover:text-cyan-400 text-xs py-2 transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Retour à la connexion
              </button>
            </form>
          ) : (
            <div className="space-y-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Nom d'utilisateur</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="text"
                        placeholder="shadow_user"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="pl-10 bg-zinc-800/50 border-zinc-700/50 text-white h-12 font-mono focus:border-cyan-500/50 focus:ring-cyan-500/20 rounded-xl transition-colors"
                        autoFocus
                        autoComplete="username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Mot de passe</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 bg-zinc-800/50 border-zinc-700/50 text-white h-12 font-mono focus:border-cyan-500/50 focus:ring-cyan-500/20 rounded-xl transition-colors"
                        autoComplete="current-password"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || !username.trim() || !password.trim()}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black font-mono font-semibold h-12 rounded-xl shadow-lg shadow-cyan-500/20 transition-colors hover:shadow-cyan-500/30 "
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                  ) : (
                    <LogIn className="w-4 h-4 mr-2" />
                  )}
                  Se connecter
                </Button>
              </form>

              {/* Separator */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent" />
                <span className="text-[10px] text-zinc-600 uppercase tracking-widest font-medium">ou</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent" />
              </div>

              <Button
                type="button"
                onClick={loginWithPasskey}
                disabled={isPasskeyLoading}
                variant="outline"
                className="w-full border-zinc-700/50 bg-zinc-800/30 text-zinc-300 hover:text-white hover:border-cyan-500/40 hover:bg-cyan-500/5 font-mono h-12 rounded-xl transition-colors group"
              >
                {isPasskeyLoading ? (
                  <div className="w-5 h-5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mr-2.5" />
                ) : (
                  <Fingerprint className="w-5 h-5 mr-2.5 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                )}
                Clé d'accès
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-2">
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center overflow-hidden">
              <img src="/icons/icon-96.png" alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <p className="text-xs font-mono font-semibold">
              <span className="text-zinc-600">&lt;</span>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">ShadowMail</span>
              <span className="text-zinc-600"> /&gt;</span>
            </p>
          </div>
          <p className="text-zinc-500 text-[10px]">Anonyme &middot; Aucune donnée enregistrée</p>
        </div>
      </div>
    </div>
  );
}
